import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { TICK_LISTS, flattenItems, type TickKind } from '../data/tickListTemplates';
import { useUserOrg } from '../lib/org';
import { isFeatureEnabled } from '../lib/features';
import {
  computeCheckStatus,
  statusLabel,
  statusBadgeClass,
  statusDotColor,
  type CheckStatus,
} from '../lib/checkStatus';
import { getSite, listTickRuns, type Site, type TickListRun } from '../lib/db';
import { cacheGet } from '../lib/cache';
import { LoadingIndicator } from '../components/LoadingIndicator';

const KINDS: TickKind[] = ['daily', 'weekly', 'monthly', 'telehandler_weekly'];

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
}

function runSummary(run: TickListRun): string {
  const tpl = TICK_LISTS[run.kind];
  const total = tpl ? flattenItems(tpl).length : Object.keys(run.responses).length;
  const responses = Object.values(run.responses);
  const pass = responses.filter((r) => r?.state === 'pass').length;
  const fail = responses.filter((r) => r?.state === 'fail').length;
  return fail > 0
    ? `${pass}/${total} OK · ${fail} issue${fail === 1 ? '' : 's'}`
    : `${pass}/${total} OK`;
}

export function ChecksMenu() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const weeklyEnabled = isFeatureEnabled(org, 'pc_site_walk');
  const [site, setSite] = useState<Site | null>(null);
  const [statuses, setStatuses] = useState<Record<TickKind, CheckStatus>>({
    daily: { state: 'never', lastDone: null },
    weekly: { state: 'never', lastDone: null },
    monthly: { state: 'never', lastDone: null },
    telehandler_weekly: { state: 'never', lastDone: null },
  });
  const [recent, setRecent] = useState<TickListRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    let haveCache = false;

    // Cache-first paint of the site object so the page header lands
    // instantly. Tick runs aren't cached (we never built that store) so
    // they still wait on the network — that's a relatively small payload.
    cacheGet<Site>('sites', siteId).then((cached) => {
      if (cancelled || !cached) return;
      haveCache = true;
      setSite(cached);
      setLoading(false);
    }).catch(() => {});

    (async () => {
      try {
        const [s, runs] = await Promise.all([
          getSite(siteId),
          listTickRuns(siteId, undefined, 100),
        ]);
        if (cancelled) return;
        setSite(s);
        const next = { ...statuses };
        for (const k of KINDS) {
          next[k] = computeCheckStatus(k, runs.filter((r) => r.kind === k));
        }
        setStatuses(next);
        setRecent(runs.slice(0, 10));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled && !haveCache) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  if (loading) {
    return (
      <AppShell title="Tick lists" back={`/sites/${siteId ?? ''}`}>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginBottom: 16, width: '100%' }}
        onClick={() => nav(`/sites/${siteId}/issues`)}
      >
        Open issues from previous checks →
      </button>
        <LoadingIndicator />
      </AppShell>
    );
  }

  if (!site) {
    return (
      <AppShell title="Tick lists" back={`/sites/${siteId ?? ''}`}>
        <div className="banner banner-error">{error ?? 'Site not found.'}</div>
      </AppShell>
    );
  }

  const renderTile = (kind: TickKind) => {
    const tpl = TICK_LISTS[kind];
    const st = statuses[kind];
    return (
      <Link
        key={kind}
        to={`/sites/${site.id}/checks/${kind}/${st.state === 'done' ? 'view' : 'run'}`}
        className="check-tile"
      >
        <span
          className="status-dot"
          style={{ background: statusDotColor(st.state) }}
          aria-label={statusLabel(st.state)}
        />
        <div className="check-tile-text">
          <p className="check-tile-title">
            {tpl.shortTitle}
            <span className={`badge ${statusBadgeClass(st.state)}`}>{statusLabel(st.state)}</span>
          </p>
          <p className="check-tile-sub">
            {tpl.subtitle}
            {st.lastDone && ` · last done ${fmtDateTime(st.lastDone)}`}
          </p>
        </div>
        <span className="chev">›</span>
      </Link>
    );
  };

  return (
    <AppShell title="Tick lists" back={`/sites/${site.id}`}>
      <h2 className="section-title">Site checks</h2>
      <div>
        {(['daily', 'weekly', 'monthly'] as TickKind[]).filter((k) => k !== 'weekly' || weeklyEnabled).map(renderTile)}
      </div>

      <h2 className="section-title" style={{ marginTop: 24 }}>Plant checks</h2>
      <div>{renderTile('telehandler_weekly')}</div>

      <h2 className="section-title" style={{ marginTop: 24 }}>
        Recent checks
        <span className="count-badge">{recent.length}</span>
      </h2>
      {recent.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No checks done on this site yet.
          </p>
        </div>
      ) : (
        <ul className="recent-checks-list">
          {recent.map((run) => {
            const tpl = TICK_LISTS[run.kind];
            return (
              <li key={run.id}>
                <Link
                  to={`/sites/${site.id}/checks/${run.kind}/view`}
                  className="recent-check-row"
                >
                  <div className="recent-check-text">
                    <p className="recent-check-title">{tpl?.title ?? run.kind}</p>
                    <p className="recent-check-summary">{runSummary(run)}</p>
                  </div>
                  <p className="recent-check-time">{fmtDateTime(run.performed_at)}</p>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </AppShell>
  );
}
